import { createRoot, type Root } from 'react-dom/client';
import { createElement } from 'react';
import type { RenderAdapter, PresentationProps } from './adapter';
import type { PresentationEvent } from '../postmessage/protocol';
import type { EmbedStyling } from '../config';
import ChatPanel from '../presentation/ChatPanel';
import FullscreenPanel from '../presentation/FullscreenPanel';
// Bundled into embed.js as a string by esbuild's `text` loader (see
// scripts/build-embed.mjs). The CSS is injected into the shadow root
// once on mount.
import PANEL_STYLES from '../presentation/styles.css';

const POSITIONS: Record<string, Record<string, string>> = {
  'bottom-right': { bottom: '0', right: '0' },
  'bottom-left': { bottom: '0', left: '0' },
  'top-right': { top: '0', right: '0' },
  'top-left': { top: '0', left: '0' },
};

/**
 * Shadow DOM render adapter.
 *
 * Alternative to the iframe adapter for CSP-strict customers whose
 * security reviews block third-party iframes. Mounts a `<div>` with a
 * **closed** shadow root on the host page and renders the React
 * `ChatPanel` tree directly inside it.
 *
 * Trade-offs vs. iframe:
 * - No origin boundary: host page JS can still see the `<div>` (but
 *   not the closed shadow root contents) and host CSS can't bleed in
 *   thanks to `:host { all: initial }` in `styles.css`.
 * - No postMessage hop: presentation events are delivered to
 *   `onEvent(handler)` via direct callback. Same `PresentationEvent`
 *   shape as the iframe adapter so `bootstrap.ts` doesn't care which
 *   adapter it has.
 * - Larger bundle: ships React + ReactDOM (iframe mode leaves them in
 *   the Next.js `/widget-presentation` route).
 */
export function createShadowAdapter(opts?: { styling?: EmbedStyling; dockEl?: HTMLElement | null }): RenderAdapter {
  const styling: EmbedStyling = opts?.styling ?? { colorScheme: 'light', vars: {}, css: null };
  const dockEl = opts?.dockEl ?? null;
  let host: HTMLDivElement | null = null;
  let shadow: ShadowRoot | null = null;
  let root: Root | null = null;
  let lastProps: PresentationProps | null = null;
  let onEventHandler: ((e: PresentationEvent) => void) | null = null;

  function emit(event: PresentationEvent) {
    onEventHandler?.(event);
  }

  function renderTree() {
    if (!root || !lastProps) return;
    // Phase 6: fullscreen layout renders the full-viewport avatar takeover
    // instead of the compact panel once expanded. Both components
    // self-branch to the same collapsed `Bubble` when `!isOverlayExpanded`
    // (see FullscreenPanel.tsx / ChatPanel.tsx), so this is the only branch
    // point needed — `layout:'panel'` (the default) always resolves to
    // `ChatPanel`, unchanged from before this field existed.
    const Component = lastProps.layout === 'fullscreen' ? FullscreenPanel : ChatPanel;
    root.render(createElement(Component, { props: lastProps, emit }));
  }

  // Sizes/positions the shadow host `<div>` itself, not just its content.
  // Collapsed (or `layout:'panel'`) keeps the original corner-pill fixed
  // positioning — content dictates size, matching every embed's behavior
  // before this field existed. Fullscreen layout, once expanded, needs the
  // HOST to go full-viewport too, or the full-bleed avatar inside
  // `FullscreenPanel` would just get clipped to a tiny auto-sized box.
  // Called on every mount + update (cheap DOM writes; a no-op visually when
  // the resolved state hasn't changed, since `cssText` is idempotent).
  function applyHostLayout(p: PresentationProps) {
    if (!host) return;
    const fullscreen = p.layout === 'fullscreen' && p.isOverlayExpanded;
    if (fullscreen && dockEl) {
      // Bounded/docked variant: fill the partner-owned container instead of
      // the viewport. `position:absolute` (not `fixed`) so it participates
      // in `dockEl`'s box rather than the browser window's.
      host.style.cssText =
        'position:absolute;z-index:2147483647;inset:0;width:100%;height:100%;' +
        'transition:inset 0.35s cubic-bezier(.22,1,.36,1)';
    } else if (fullscreen) {
      host.style.cssText =
        'position:fixed;z-index:2147483647;inset:0;width:100vw;height:100vh;' +
        'transition:inset 0.35s cubic-bezier(.22,1,.36,1)';
    } else {
      const pos = POSITIONS[p.position] ?? POSITIONS['bottom-right'];
      const posCss = Object.entries(pos)
        .map(([k, v]) => `${k}:${v}`)
        .join(';');
      host.style.cssText = `position:fixed;z-index:2147483647;${posCss}`;
    }
    // The shadow root is closed, so the host page has no way to observe
    // expansion state for its own layout (e.g. shrinking content to make
    // room for the dock). Reflect it as an attribute on the partner-visible
    // element — the dock container when docked, the host node otherwise —
    // so partner CSS can key on it:
    //   body:has(#mobeus-dock[data-mobeus-expanded]) { margin-right: …; }
    (dockEl ?? host).toggleAttribute('data-mobeus-expanded', p.isOverlayExpanded);
    // `cssText` above replaces the whole inline style attribute, so the
    // per-instance `styling.vars` custom-property overrides need reapplying
    // every time — they aren't expressible in `cssText` itself since their
    // key set is dynamic.
    for (const [k, v] of Object.entries(styling.vars)) {
      host.style.setProperty(`--mobeus-${k}`, v);
    }
  }

  return {
    mount(props) {
      if (host) return; // idempotent — second mount is a no-op
      lastProps = props;

      host = document.createElement('div');
      host.setAttribute('data-mobeus-shadow-host', '');
      // Drives the light/dark palette in styles.css (:host([data-mobeus-scheme])).
      host.setAttribute('data-mobeus-scheme', styling.colorScheme);
      applyHostLayout(props);
      if (dockEl) {
        // `host` is `position:absolute` when docked (see applyHostLayout),
        // which requires `dockEl` itself to be a positioning context —
        // otherwise `host` would escape to whatever ancestor IS positioned
        // (or the viewport) instead of filling this container. Only touch
        // it if the partner's own CSS left it `static`.
        if (getComputedStyle(dockEl).position === 'static') {
          dockEl.style.position = 'relative';
        }
        dockEl.appendChild(host);
      } else {
        document.body.appendChild(host);
      }

      // `closed` mode means `host.shadowRoot` is null from the outside.
      // Host page scripts can't poke ChatPanel internals through it. `::part`
      // and inherited custom properties still work across a closed root, so
      // the styling surface is unaffected by the isolation.
      shadow = host.attachShadow({ mode: 'closed' });

      const style = document.createElement('style');
      style.textContent = PANEL_STYLES;
      shadow.appendChild(style);

      // Customer-supplied raw CSS, appended AFTER the base sheet so it wins.
      // Injected inside the shadow root: it can target the stable `.mobeus-*`
      // element classes without escaping the isolation boundary.
      if (styling.css) {
        const custom = document.createElement('style');
        custom.setAttribute('data-mobeus-custom', '');
        custom.textContent = styling.css;
        shadow.appendChild(custom);
      }

      // Deliberately NOT given explicit sizing: the collapsed `Bubble` and
      // compact `ChatPanel` both rely on this being a plain, unpositioned
      // block <div> that shrink-wraps to their own intrinsic/fixed-pixel
      // size (so `host`'s shrink-to-fit sizing in the collapsed corner-pill
      // state works). `FullscreenPanel` (Phase 6) instead self-anchors with
      // its own `position:fixed;inset:0` — see that component — rather than
      // depending on this element or `host` for sizing, to avoid exactly
      // this kind of coupling.
      const mountPoint = document.createElement('div');
      shadow.appendChild(mountPoint);

      root = createRoot(mountPoint);
      renderTree();
    },
    update(partial) {
      if (!lastProps) return;
      lastProps = { ...lastProps, ...partial };
      renderTree();
      applyHostLayout(lastProps);
    },
    unmount() {
      root?.unmount();
      host?.remove();
      host = null;
      shadow = null;
      root = null;
      lastProps = null;
    },
    onEvent(handler) {
      onEventHandler = handler;
    },
    getAvatarMountEl() {
      // Fresh query every call, not cached: ChatPanel mounts/unmounts this
      // node as `avatarEnabled`/`isOverlayExpanded` change (it only renders
      // inside the expanded panel), so a stale cached reference would go
      // stale the moment the panel collapses and React tears the node down.
      if (!shadow) return null;
      return shadow.querySelector('[data-mobeus-avatar-mount]') as HTMLElement | null;
    },
  };
}
